import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  Typography,
  dialogClasses,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { AccountContext } from "../../store/AccountContext";
import { LoggerContext } from "../../store/LoggerContext";
import { color, fontWeight } from "../../theme/tokens";
import { getUserMessage } from "../../utils/errorMessages";
import {
  logWebTrialEvent,
  type WebTrialParams,
} from "../../utils/getAppAnalytics";

/**
 * #1261 (Free Feed, Increment 3, Slice 2): the confirm step before a website
 * trial, for someone on a computer with no smartphone or tablet. Board
 * FF-1260-C, screen 9 ("the same confirm step as B"), shown as a dialog
 * (default taken in the plan) on My Account and on sign-up's last screen.
 *
 * **SUBSCRIPTIONS / MEMBERSHIP AND PAYMENTS ESCALATION CLASS.** START MY FREE
 * TRIAL starts the trial through `assignTrialMembership`
 * (`store/AccountContext.tsx`), which sends `POST /payments` with the trial
 * product the server offers this account: the same call the app's START
 * TRIAL makes (`FFCustomerApp/src/Stores/Mobx/AccountStore.ts`, `startTrial`).
 * The server writes the £0 trial record; no card, no Stripe. It then opens
 * the videos page. The start and its `free-trial` event are copied from the
 * old My Account buttons (`MyAccount/Overview.js` `takeTrial`, app-web
 * `889209c`, before #1246). A failed start shows its reason in the dialog
 * (the old buttons used a toast; new files can't import
 * react-toast-notifications under the PM5 D4 ratchet, `eslint.config.mjs`),
 * as `ChangeEmail.tsx` shows its error.
 *
 * An account with no mobile on file is asked to add one first (Ellaie, 3 Nov):
 * START is replaced by ADD MY MOBILE, which opens today's add-a-mobile page.
 * The test for "no mobile" is Overview.js's Add Phone / Change Phone one.
 *
 * Every colour is named (app-web CLAUDE.md: MUI's defaults assume a dark
 * background). The panel is white, as the board draws it.
 */

// Exact words from board FF-1260-C, screen 9.
export const CONFIRM_HEADING = "Start your free trial on the website";
const CONFIRM_LINE =
  "Your 3-day free trial starts now. Watch every episode here on our website until it ends. No card needed.";
const APP_LINE =
  "Got a smartphone or tablet? Start your trial in the FarmFLiX app instead, and watch it there.";
const START_BUTTON = "START MY FREE TRIAL";
const BACK_BUTTON = "GO BACK";
// Not drawn on the board (the plan's "can't settle from here"): the builder's
// words, listed in the PR for Ellaie.
const NO_MOBILE_LINE =
  "Before your trial starts, add your mobile number to your account. Then come back here and press START MY TRIAL ON THE WEBSITE again.";
const ADD_MOBILE_BUTTON = "ADD MY MOBILE";

// Board: white panel, 32px padding, 14px between rows, at most 720px wide.
// No corners drawn on the board.
const dialogSx: SxProps<Theme> = {
  [`& .${dialogClasses.paper}`]: {
    backgroundColor: color.white,
    color: color.textPrimary,
    borderRadius: 0,
    maxWidth: "720px",
    padding: { xs: "20px", sm: "32px" },
    boxSizing: "border-box",
  },
};

const bodySx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};

// Board: 28.8px, weight 500.
const headingSx: SxProps<Theme> = {
  color: color.textPrimary,
  fontSize: "1.8rem",
  lineHeight: 1.2,
  fontWeight: fontWeight.medium,
  margin: 0,
};

const lineSx: SxProps<Theme> = {
  color: color.textPrimary,
  lineHeight: 1.5,
};

const appLineSx: SxProps<Theme> = {
  color: color.textSecondary,
  lineHeight: 1.5,
};

const buttonsSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  marginTop: "6px",
};

// Board: pill buttons, 44px high, 14px weight 500, 32px either side; full
// width on a phone. The theme gives the 2px border and the 25px corners.
const buttonBaseSx = {
  flex: { xs: "1 1 100%", sm: "0 0 auto" },
  minHeight: "44px",
  margin: 0,
  padding: "0 32px",
  fontSize: "0.875rem",
  fontWeight: fontWeight.medium,
  letterSpacing: "0.5px",
};

// Flat, as the board draws it (MUI's contained button is raised).
const startSx: SxProps<Theme> = {
  ...buttonBaseSx,
  color: color.ffNavy,
  boxShadow: "none",
  "&:hover": { color: color.ffNavy, boxShadow: "none" },
  "&.Mui-disabled": {
    color: color.ffNavy,
    opacity: 0.6,
  },
};

const backSx: SxProps<Theme> = {
  ...buttonBaseSx,
  color: color.ffNavy,
  borderColor: color.ffNavy,
  "&:hover": { color: color.ffNavy, borderColor: color.ffNavy },
};

type WebTrialConfirmProps = {
  open: boolean;
  /** Closes the dialog (GO BACK, or a click outside it). */
  onClose: () => void;
  /** Where the card was, for the events. */
  surface: WebTrialParams["surface"];
  /** The member's status, for the events. */
  memberStatusId: string;
};

const WebTrialConfirm = ({
  open,
  onClose,
  surface,
  memberStatusId,
}: WebTrialConfirmProps) => {
  const { userProfile, assignTrialMembership } = useContext(AccountContext);
  const { logMetricEvent } = useContext(LoggerContext);
  const [error, setError] = useState<string>();
  // START is disabled from the first press until the answer, so a double
  // press sends one request (the server also refuses a second £0 trial
  // within 2 minutes, PaymentController.ts).
  const [starting, setStarting] = useState(false);

  // The card keeps this mounted and only flips `open` (WebTrialCard.tsx), so
  // an error from the last try is cleared whenever the step opens or closes,
  // including a click outside or Escape.
  useEffect(() => {
    setError(undefined);
  }, [open]);

  const noMobile = !userProfile?.countryCode || !userProfile?.phoneNumber;

  const logChoice = (choice: WebTrialParams["choice"]) =>
    logWebTrialEvent("web_trial_confirm_click", {
      member_status: memberStatusId,
      surface,
      choice,
    });

  const start = async () => {
    setStarting(true);
    setError(undefined);
    logChoice("start");
    try {
      // #1225: the pixel's StartTrial uses the event ID the server's
      // Facebook listener builds from the trial payment, so Meta counts it
      // once. Copied from Overview.js's takeTrial at 889209c.
      const paymentId = await assignTrialMembership();
      logMetricEvent(
        {
          type: "free-trial",
          payload: { route: "no-phone", device_type: "computer", surface },
        },
        paymentId ? `start_trial:payment:${paymentId}` : undefined,
      );
    } catch (e) {
      // No email or phone in the log line.
      console.error(
        "[WebTrial] trial start failed",
        { surface, member_status: memberStatusId },
        e,
      );
      // getUserMessage passes AccountContext's own sentences through and
      // turns a server reply body (a 500 arrives as a plain object) into
      // member words.
      setError(getUserMessage(e));
      setStarting(false);
    }
  };

  const back = () => {
    logChoice("back");
    setError(undefined);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={starting ? undefined : onClose}
      aria-labelledby="web-trial-confirm-heading"
      sx={dialogSx}
    >
      <Box sx={bodySx}>
        <Typography id="web-trial-confirm-heading" variant="h2" sx={headingSx}>
          {CONFIRM_HEADING}
        </Typography>
        {error && (
          <Alert severity="error" variant="filled">
            {error}
          </Alert>
        )}
        <Typography variant="body1" sx={lineSx}>
          {noMobile ? NO_MOBILE_LINE : CONFIRM_LINE}
        </Typography>
        <Typography variant="body1" sx={appLineSx}>
          {APP_LINE}
        </Typography>
        <Box sx={buttonsSx}>
          {noMobile ? (
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/myaccount/add-phone"
              onClick={() => logChoice("add_mobile")}
              sx={startSx}
            >
              {ADD_MOBILE_BUTTON}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              disabled={starting}
              onClick={start}
              sx={startSx}
            >
              {START_BUTTON}
            </Button>
          )}
          <Button
            variant="outlined"
            disabled={starting}
            onClick={back}
            sx={backSx}
          >
            {BACK_BUTTON}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default WebTrialConfirm;
